/**
 * User-facing copy for every error code the backend can answer with.
 *
 * Single source of truth shared by admin and PWA: each app merges
 * `API_ERROR_MESSAGES[locale]` into its `apiErrors` i18n namespace, and
 * `translateApiError` resolves `apiErrors:codes.<code>` (and, for a handful of
 * overloaded codes, `apiErrors:codes.<code>__<httpStatus>`).
 *
 * Why not render `ApiError.message`? That string is the backend's developer
 * English (or, for some legacy use cases, Spanish) — it never follows the
 * active UI language. `api-error-messages.spec.ts` fails when a `DomainError`
 * code exists in apps/backend without a translation here, so new codes cannot
 * silently fall back to raw backend text.
 *
 * Writing guidelines: say what happened and what the user can do next, without
 * leaking implementation detail (ids, enum names). `es` uses tuteo.
 */

export type ApiErrorLocale = 'en' | 'es';

export interface ApiErrorMessages {
  /** `apiErrors:codes.*` — keyed by backend error code (optionally `<code>__<status>`). */
  codes: Record<string, string>;
  /** `apiErrors:fields.*` — display names of check-in field ids (`parking_zone`) used in `{{field}}`. */
  fields: Record<string, string>;
  /**
   * `apiErrors:status.*` — generic copy by HTTP status when the body has no
   * known code. Only statuses that say something the call-site fallback
   * cannot (session, permission, missing, throttled, server down); 400/409/422
   * deliberately fall through to the call-site `fallback`.
   */
  status: Record<string, string>;
}

const en: ApiErrorMessages = {
  codes: {
    // --- Authentication / session -------------------------------------------------
    invalid_credentials: 'Incorrect email or password.',
    account_locked: 'Account temporarily locked. Try again in 15 minutes.',
    invalid_invitation: 'This invitation is invalid or has already been used.',
    expired_invitation: 'This invitation has expired. Ask your administrator for a new one.',
    invalid_password: 'The current password is incorrect.',
    weak_password: 'Password does not meet the minimum requirements.',
    invalid_refresh_token: 'Your session expired. Please sign in again.',
    organization_suspended: 'Organization suspended. Contact your administrator.',
    refresh_in_progress: 'Refreshing your session. Please try again in a moment.',
    invalid_reset_token: 'The recovery link is invalid or has expired.',
    invalid_mfa_token: 'Your MFA session expired. Please sign in again.',
    mfa_email_otp_cooldown: 'Please wait before requesting another code.',
    mfa_email_otp_expired: 'Your code has expired. Request a new one.',
    rate_limited: 'Too many attempts. Wait a minute and try again.',
    invalid_station_token: 'Invalid or revoked station device token.',
    subscription_access_blocked:
      "Your organization's subscription is suspended. New vehicles cannot be checked in.",

    // --- Generic request problems -------------------------------------------------
    forbidden: "You don't have permission to do this.",
    forbidden__400: 'The submitted data is invalid.',
    forbidden__422: 'The submitted data is invalid.',
    forbidden__409: "This action isn't available in the current state.",
    forbidden_role: "Your role isn't allowed to do this.",
    not_found: 'The requested item was not found.',
    name_taken: 'That name is already in use.',
    invalid_input: 'The submitted data is invalid.',
    invalid_request: 'The request is invalid. Check the data and try again.',
    LOCATION_ACCESS_DENIED: "You don't have access to this location.",
    LOCATION_INACTIVE: 'This location is inactive.',
    unexpected_error: 'An unexpected error occurred.',
    network_error: "We couldn't reach the server.",
    photo_upload_failed: "Couldn't upload the photos. Try again.",
    unknown: 'An unexpected error occurred.',

    // --- Users, invitations, locations --------------------------------------------
    duplicate_email: 'This email is already registered.',
    duplicate_invitation: 'There is already a pending invitation for this email.',
    cross_org_user: 'That user belongs to a different organization.',
    invalid_role_for_assignment: "This user's role can't be assigned to a location.",
    location_access_denied: "You don't have access to this location.",
    location_inactive: 'This location is inactive.',
    last_platform_super_admin: "You can't deactivate the last active super admin.",
    zone_capacity_required:
      "This zone needs a capacity value because the location's vehicle capacity is set to {{locationCapacity}}.",
    zone_capacity_exceeds_location:
      "You can't assign {{attemptedTotal}} vehicles: this location has capacity for {{locationCapacity}} and {{currentAssigned}} are already assigned.",
    location_capacity_below_assigned:
      "You can't set the vehicle capacity to {{requestedCapacity}}: active zones already have {{assignedCapacity}} vehicles assigned.",

    // --- Configuration ------------------------------------------------------------
    invalid_tariff_amount: 'The tariff amount is not valid.',
    niche_mismatch: "This option isn't available for this location's business type.",
    niche_change_blocked: "The business type can't be changed while there are active tickets.",
    niche_field_mismatch:
      "Some of the submitted fields don't apply to this location's business type.",
    invalid_tip: 'The tip amount is not valid.',
    comps_disabled: 'Complimentary tickets are not enabled for this location.',
    comp_not_authorized: 'Your role is not authorized to apply complimentary tickets here.',
    invalid_qr_prefix: 'The QR prefix must be 2 to 8 letters or digits (A-Z, 0-9).',
    qr_batch_size_exceeded: 'That batch is too large. Request fewer QR codes at a time.',
    empty_qr_batch: 'The batch must contain at least one QR code.',
    station_location_not_found: 'Location not found.',
    station_device_not_found: 'Station device not found.',

    // --- Tickets, vehicles, photos ------------------------------------------------
    duplicate_active_ticket: 'There is already an active ticket for this plate.',
    qr_tag_conflict: 'This QR tag is already in use by an active ticket at this location.',
    ticket_type_disabled: 'This ticket type is not enabled for this location.',
    required_field_missing: 'A required field is missing: {{field}}.',
    // Wording used when the error arrives without the body fields (see translateErrorCode).
    required_field_missing__generic: 'A required field is missing.',
    photo_count_invalid: 'The number of photos is not valid.',
    photo_token_invalid: 'The photo upload expired or is invalid. Take the photos again.',
    photo_angle_incomplete: 'Some required photo angles are missing.',
    incident_photo_count_invalid: 'Too many incident photos were attached.',
    incident_photo_token_invalid:
      'The incident photo upload expired or is invalid. Take the photos again.',
    visitor_ticket_not_found: 'We could not find this ticket.',
    visitor_retrieval_not_found: 'We could not find this retrieval request.',
    ticket_not_found: 'Ticket not found.',
    ticket_already_claimed: 'Another valet already claimed this ticket.',
    ticket_already_delivered: 'This ticket was already delivered.',
    ticket_not_in_transit: "This ticket isn't in transit.",
    ticket_not_closed: 'Only closed tickets can be reopened.',
    reopen_confirmation_required: 'Confirm that you want to reopen this ticket.',
    valet_not_active: 'That valet is not active.',
    valet_not_operator: 'The selected user is not an operator.',
    valet_not_assigned_to_location: "That valet isn't assigned to this location.",
    max_exits_exceeded: 'The maximum number of exits for this ticket has been reached.',
    vehicle_out: 'The vehicle is already out. Register its re-entry first.',
    open_exit_exists: 'This ticket already has an open exit.',
    no_open_exit: 'This ticket has no open exit.',
    exit_conflict: 'Another exit was registered at the same time. Try again.',
    idempotency_key_reused: 'This request was already used for a different check-in.',
    ticket_request_in_flight: 'This request is still being processed. Try again in a moment.',

    // --- Payments -----------------------------------------------------------------
    already_paid: 'This ticket has already been paid.',
    payment_unavailable: 'Online payment is not available right now.',
    payment_required: 'Payment is required before the vehicle can be retrieved.',
    cash_not_applicable: 'This ticket has no charge, so a cash payment does not apply.',
    payment_already_completed: 'The payment was already completed.',
    delay_recharge_required: 'The delay charge must be paid before delivery.',
    delay_recharge_not_applicable: 'No delay charge is owed for this ticket.',
    billing_not_self: 'This ticket is covered by the host and cannot be paid here.',

    // --- Events -------------------------------------------------------------------
    event_invalid_status_transition: "The event can't move to that status from its current one.",
    event_not_open_for_checkin: 'The event is not open for check-in.',
    event_field_not_editable: "This can't be edited in the event's current status.",
    event_guest_invalid_billing_mode: 'The guest billing mode is not valid for this event.',
    event_staff_already_assigned: 'This user is already staffed on this event.',
    event_staff_not_eligible: 'This user has no active assignment to the event location.',
    event_guest_already_stamped: 'This guest is already linked to a ticket.',
    event_guest_ticket_mismatch: 'This ticket does not belong to the event.',
    event_close_blocked_unsettled_payments:
      'The event has unsettled payments. Settle them or force-close the event.',
    event_report_not_available: 'The report is available once the event is closed.',
    event_already_closed: 'The event was already closed.',
    event_activation_blocked_missing_billing_mode:
      'Every guest needs a billing mode before the event can be activated.',
    charge_mode_mismatch: 'A tariff override only applies to per-attendee events.',

    // --- Memberships --------------------------------------------------------------
    membership_invalid_status_transition: "The membership can't move to that status.",
    membership_payment_invalid_status_transition: "The payment can't move to that status.",
    membership_duplicate_plate: 'That plate is already registered to another member.',
    membership_enrollment_requires_vehicle:
      'Add at least one vehicle before enrolling this member.',
    membership_already_enrolled:
      'This member already has an active membership. Cancel it before enrolling in a new plan.',
    membership_plan_inactive: 'This membership plan is no longer available.',
    membership_enrollment_link_expired: 'This enrollment link has expired or is no longer valid.',
    membership_otp_invalid: 'Incorrect verification code.',
    membership_otp_exhausted: 'Too many incorrect codes. Request a new code.',
    membership_otp_not_verified: 'Verify your code before continuing.',
    membership_otp_expired: 'This verification code has expired. Request a new one.',
    membership_not_recoverable: "This membership can't be recovered. Contact the organization.",

    // --- Subscription & billing ---------------------------------------------------
    plan_inactive: "That plan is deactivated and can't be assigned.",
    feature_not_in_plan:
      "Your current plan doesn't include this feature. Upgrade your plan to use it.",
    subscription_already_exists: 'This organization already has a subscription.',
    not_manual: 'This invoice is collected automatically and cannot be paid manually.',
    earlier_open_invoice_exists: 'Pay the oldest open invoice first.',
    overage_charge_invalid_status_transition: "The overage charge can't move to that status.",
    overage_billing_not_provisioned: 'Billing is not fully set up for this subscription yet.',
    ticket_export_already_active:
      'An export is already in progress. Wait for it to finish before starting another.',
    ticket_export_not_found: 'Export not found.',
    ticket_export_invalid_status_transition: "The export can't move to that status.",
  },
  fields: {
    plate: 'Plate',
    first_name: 'First name',
    last_name: 'Last name',
    phone: 'Phone',
    visitor_email: 'Visitor email',
    room_number: 'Room number',
    visitor_type: 'Visitor type',
    parking_zone: 'Parking zone',
    valet_notes: 'Valet notes',
  },
  status: {
    '401': 'Your session expired. Please sign in again.',
    '403': "You don't have permission to do this.",
    '404': 'The requested item was not found.',
    '429': 'Too many attempts. Wait a minute and try again.',
    server: 'Something went wrong on our side. Please try again in a moment.',
  },
};

const es: ApiErrorMessages = {
  codes: {
    // --- Autenticación / sesión ---------------------------------------------------
    invalid_credentials: 'Correo o contraseña incorrectos.',
    account_locked: 'Cuenta bloqueada temporalmente. Inténtalo de nuevo en 15 minutos.',
    invalid_invitation: 'Esta invitación no es válida o ya fue utilizada.',
    expired_invitation: 'Esta invitación expiró. Pide a tu administrador una nueva.',
    invalid_password: 'La contraseña actual es incorrecta.',
    weak_password: 'La contraseña no cumple los requisitos mínimos.',
    invalid_refresh_token: 'Tu sesión expiró. Inicia sesión de nuevo.',
    organization_suspended: 'Organización suspendida. Contacta a tu administrador.',
    refresh_in_progress: 'Estamos renovando tu sesión. Inténtalo de nuevo en un momento.',
    invalid_reset_token: 'El enlace de recuperación no es válido o ya expiró.',
    invalid_mfa_token: 'Tu sesión de verificación expiró. Inicia sesión de nuevo.',
    mfa_email_otp_cooldown: 'Espera un momento antes de solicitar otro código.',
    mfa_email_otp_expired: 'Tu código expiró. Solicita uno nuevo.',
    rate_limited: 'Demasiados intentos. Espera un minuto e inténtalo de nuevo.',
    invalid_station_token: 'El token del dispositivo de estación no es válido o fue revocado.',
    subscription_access_blocked:
      'La suscripción de tu organización está suspendida. No se pueden registrar nuevos vehículos.',

    // --- Problemas genéricos de la solicitud --------------------------------------
    forbidden: 'No tienes permiso para realizar esta acción.',
    forbidden__400: 'Los datos enviados no son válidos.',
    forbidden__422: 'Los datos enviados no son válidos.',
    forbidden__409: 'Esta acción no está disponible en el estado actual.',
    forbidden_role: 'Tu rol no permite realizar esta acción.',
    not_found: 'No se encontró el elemento solicitado.',
    name_taken: 'Ese nombre ya está en uso.',
    invalid_input: 'Los datos enviados no son válidos.',
    invalid_request: 'La solicitud no es válida. Revisa los datos e inténtalo de nuevo.',
    LOCATION_ACCESS_DENIED: 'No tienes acceso a esta ubicación.',
    LOCATION_INACTIVE: 'Esta ubicación está inactiva.',
    unexpected_error: 'Ocurrió un error inesperado.',
    network_error: 'No pudimos conectarnos con el servidor.',
    photo_upload_failed: 'No se pudieron subir las fotos. Inténtalo de nuevo.',
    unknown: 'Ocurrió un error inesperado.',

    // --- Usuarios, invitaciones, ubicaciones --------------------------------------------
    duplicate_email: 'Este correo ya está registrado.',
    duplicate_invitation: 'Ya existe una invitación pendiente para este correo.',
    cross_org_user: 'Ese usuario pertenece a otra organización.',
    invalid_role_for_assignment: 'El rol de este usuario no se puede asignar a una ubicación.',
    location_access_denied: 'No tienes acceso a esta ubicación.',
    location_inactive: 'Esta ubicación está inactiva.',
    last_platform_super_admin: 'No puedes desactivar al último super administrador activo.',
    zone_capacity_required:
      'Esta zona necesita una capacidad porque la ubicación tiene definida una capacidad de vehículos de {{locationCapacity}}.',
    zone_capacity_exceeds_location:
      'No puedes asignar {{attemptedTotal}} vehículos: esta ubicación tiene capacidad para {{locationCapacity}} y ya hay {{currentAssigned}} asignados.',
    location_capacity_below_assigned:
      'No puedes bajar la capacidad de vehículos a {{requestedCapacity}}: las zonas activas ya tienen {{assignedCapacity}} vehículos asignados.',

    // --- Configuración ------------------------------------------------------------
    invalid_tariff_amount: 'El monto de la tarifa no es válido.',
    niche_mismatch: 'Esta opción no está disponible para el tipo de negocio de esta ubicación.',
    niche_change_blocked: 'No se puede cambiar el tipo de negocio mientras haya tickets activos.',
    niche_field_mismatch:
      'Algunos campos enviados no aplican al tipo de negocio de esta ubicación.',
    invalid_tip: 'El monto de la propina no es válido.',
    comps_disabled: 'Los tickets de cortesía no están habilitados en esta ubicación.',
    comp_not_authorized: 'Tu rol no está autorizado para aplicar cortesías aquí.',
    invalid_qr_prefix: 'El prefijo del QR debe tener de 2 a 8 letras o dígitos (A-Z, 0-9).',
    qr_batch_size_exceeded: 'El lote es demasiado grande. Solicita menos códigos QR a la vez.',
    empty_qr_batch: 'El lote debe contener al menos un código QR.',
    station_location_not_found: 'No se encontró la ubicación.',
    station_device_not_found: 'No se encontró el dispositivo de estación.',

    // --- Tickets, vehículos, fotos -----------------------------------------------
    duplicate_active_ticket: 'Ya hay un ticket activo para esta placa.',
    qr_tag_conflict: 'Este código QR ya está en uso por un ticket activo en esta ubicación.',
    ticket_type_disabled: 'Este tipo de ticket no está habilitado en esta ubicación.',
    required_field_missing: 'Falta un campo obligatorio: {{field}}.',
    required_field_missing__generic: 'Falta un campo obligatorio.',
    photo_count_invalid: 'La cantidad de fotos no es válida.',
    photo_token_invalid: 'La carga de fotos expiró o no es válida. Toma las fotos de nuevo.',
    photo_angle_incomplete: 'Faltan algunos ángulos de foto obligatorios.',
    incident_photo_count_invalid: 'Se adjuntaron demasiadas fotos de incidente.',
    incident_photo_token_invalid:
      'La carga de fotos del incidente expiró o no es válida. Toma las fotos de nuevo.',
    visitor_ticket_not_found: 'No encontramos este ticket.',
    visitor_retrieval_not_found: 'No encontramos esta solicitud de entrega.',
    ticket_not_found: 'No se encontró el ticket.',
    ticket_already_claimed: 'Otro valet ya tomó este ticket.',
    ticket_already_delivered: 'Este ticket ya fue entregado.',
    ticket_not_in_transit: 'Este ticket no está en tránsito.',
    ticket_not_closed: 'Solo se pueden reabrir tickets cerrados.',
    reopen_confirmation_required: 'Confirma que quieres reabrir este ticket.',
    valet_not_active: 'Ese valet no está activo.',
    valet_not_operator: 'El usuario seleccionado no es un operador.',
    valet_not_assigned_to_location: 'Ese valet no está asignado a esta ubicación.',
    max_exits_exceeded: 'Se alcanzó el máximo de salidas para este ticket.',
    vehicle_out: 'El vehículo ya está afuera. Registra primero su reingreso.',
    open_exit_exists: 'Este ticket ya tiene una salida abierta.',
    no_open_exit: 'Este ticket no tiene una salida abierta.',
    exit_conflict: 'Se registró otra salida al mismo tiempo. Inténtalo de nuevo.',
    idempotency_key_reused: 'Esta solicitud ya se usó para otro registro de ingreso.',
    ticket_request_in_flight: 'Esta solicitud aún se está procesando. Inténtalo en un momento.',

    // --- Pagos --------------------------------------------------------------------
    already_paid: 'Este ticket ya fue pagado.',
    payment_unavailable: 'El pago en línea no está disponible en este momento.',
    payment_required: 'Se requiere el pago antes de poder entregar el vehículo.',
    cash_not_applicable: 'Este ticket no tiene cobro, por lo que no aplica un pago en efectivo.',
    payment_already_completed: 'El pago ya fue completado.',
    delay_recharge_required: 'Debes pagar el recargo por demora antes de la entrega.',
    delay_recharge_not_applicable: 'Este ticket no tiene recargo por demora pendiente.',
    billing_not_self: 'Este ticket lo cubre el anfitrión y no se puede pagar aquí.',

    // --- Eventos ------------------------------------------------------------------
    event_invalid_status_transition: 'El evento no puede pasar a ese estado desde el actual.',
    event_not_open_for_checkin: 'El evento no está abierto para ingresos.',
    event_field_not_editable: 'Esto no se puede editar en el estado actual del evento.',
    event_guest_invalid_billing_mode:
      'El modo de cobro del invitado no es válido para este evento.',
    event_staff_already_assigned: 'Este usuario ya está asignado a este evento.',
    event_staff_not_eligible:
      'Este usuario no tiene una asignación activa en la ubicación del evento.',
    event_guest_already_stamped: 'Este invitado ya está vinculado a un ticket.',
    event_guest_ticket_mismatch: 'Este ticket no pertenece al evento.',
    event_close_blocked_unsettled_payments:
      'El evento tiene pagos pendientes. Sáldalos o fuerza el cierre del evento.',
    event_report_not_available: 'El reporte está disponible cuando el evento se cierra.',
    event_already_closed: 'El evento ya fue cerrado.',
    event_activation_blocked_missing_billing_mode:
      'Cada invitado necesita un modo de cobro antes de activar el evento.',
    charge_mode_mismatch: 'Una tarifa especial solo aplica a eventos cobrados por asistente.',

    // --- Membresías ---------------------------------------------------------------
    membership_invalid_status_transition: 'La membresía no puede pasar a ese estado.',
    membership_payment_invalid_status_transition: 'El pago no puede pasar a ese estado.',
    membership_duplicate_plate: 'Esa placa ya está registrada en otro miembro.',
    membership_enrollment_requires_vehicle:
      'Agrega al menos un vehículo antes de inscribir a este miembro.',
    membership_already_enrolled:
      'Este miembro ya tiene una membresía activa. Cancélala antes de inscribirlo en un nuevo plan.',
    membership_plan_inactive: 'Este plan de membresía ya no está disponible.',
    membership_enrollment_link_expired: 'Este enlace de inscripción expiró o ya no es válido.',
    membership_otp_invalid: 'Código de verificación incorrecto.',
    membership_otp_exhausted: 'Demasiados códigos incorrectos. Solicita un código nuevo.',
    membership_otp_not_verified: 'Verifica tu código antes de continuar.',
    membership_otp_expired: 'Este código de verificación expiró. Solicita uno nuevo.',
    membership_not_recoverable: 'Esta membresía no se puede recuperar. Contacta a la organización.',

    // --- Suscripción y facturación ------------------------------------------------
    plan_inactive: 'Ese plan está desactivado y no se puede asignar.',
    feature_not_in_plan: 'Tu plan actual no incluye esta función. Mejora tu plan para usarla.',
    subscription_already_exists: 'Esta organización ya tiene una suscripción.',
    not_manual: 'Esta factura se cobra automáticamente y no se puede pagar de forma manual.',
    earlier_open_invoice_exists: 'Paga primero la factura abierta más antigua.',
    overage_charge_invalid_status_transition: 'El cargo por excedente no puede pasar a ese estado.',
    overage_billing_not_provisioned:
      'La facturación de esta suscripción aún no está configurada por completo.',
    ticket_export_already_active:
      'Ya hay una exportación en curso. Espera a que termine antes de iniciar otra.',
    ticket_export_not_found: 'No se encontró la exportación.',
    ticket_export_invalid_status_transition: 'La exportación no puede pasar a ese estado.',
  },
  fields: {
    plate: 'Placa',
    first_name: 'Nombre',
    last_name: 'Apellido',
    phone: 'Teléfono',
    visitor_email: 'Correo del visitante',
    room_number: 'Número de habitación',
    visitor_type: 'Tipo de visitante',
    parking_zone: 'Zona de estacionamiento',
    valet_notes: 'Notas del valet',
  },
  status: {
    '401': 'Tu sesión expiró. Inicia sesión de nuevo.',
    '403': 'No tienes permiso para realizar esta acción.',
    '404': 'No se encontró el elemento solicitado.',
    '429': 'Demasiados intentos. Espera un minuto e inténtalo de nuevo.',
    server: 'Algo salió mal de nuestro lado. Inténtalo de nuevo en un momento.',
  },
};

export const API_ERROR_MESSAGES: Record<ApiErrorLocale, ApiErrorMessages> = { en, es };

/**
 * Adds the shared `codes` / `status` copy to an app's `apiErrors` namespace
 * without clobbering keys the app already owns (e.g. admin's
 * `duplicateEmailPlatform`). App-owned keys win on conflict.
 */
export function withApiErrorMessages(
  locale: ApiErrorLocale,
  namespaces: Record<string, unknown>,
): Record<string, unknown> {
  const own = (namespaces.apiErrors ?? {}) as Record<string, unknown>;
  return { ...namespaces, apiErrors: { ...API_ERROR_MESSAGES[locale], ...own } };
}
